// ---------------------------------------------------------------------------
// EventFlowOverlay -- renders pill-shaped badges below the selected element
// showing event -> flow -> data connections. Tracks DOM position via
// data-element-id (same pattern as SelectionOverlay).
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useCrossReferenceIndex, type ElementEventEntry } from '@/lib/cross-reference'
import { resolveVisibleElement } from '@/features/page-editor/lib/dom-utils'
import {
  MousePointerClick,
  FormInput,
  Focus,
  EyeOff,
  Send,
  Move,
  Zap,
  ArrowRight,
  Workflow,
  Database,
} from 'lucide-react'

// Event type -> icon mapping (mirrors event-binding-row.tsx)
const EVENT_ICONS: Record<string, React.FC<{ className?: string; size?: number }>> = {
  tap: MousePointerClick,
  click: MousePointerClick,
  change: FormInput,
  focus: Focus,
  blur: EyeOff,
  submit: Send,
  hover: Move,
}

interface BadgePosition {
  top: number
  left: number
  width: number
  height: number
}

export function EventFlowOverlay() {
  const selectedIds = useEditorStore((s) => s.selectedIds)
  const isPreview = useEditorStore((s) => s.isPreview)
  const selectedBlockName = useEditorStore((s) => s.selectedBlockName)
  const activeBreakpoint = useEditorStore((s) => s.activeBreakpoint)
  const zoom = useEditorStore((s) => s.zoom)
  const flows = useEditorStore((s) => s.flows)
  const crossRef = useCrossReferenceIndex()
  const overlayRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<BadgePosition | null>(null)
  const [visible, setVisible] = useState(false)

  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null

  // Get event bindings for selected element
  const bindings: ElementEventEntry[] = useMemo(() => {
    if (!selectedId) return []
    return crossRef.elementEventIndex.get(selectedId) ?? []
  }, [selectedId, crossRef])

  // Summarize data targets for each flow
  const flowDataTargets = useMemo(() => {
    const targets = new Map<string, string>()
    for (const b of bindings) {
      const flow = flows[b.flowName]
      if (!flow?.steps) continue
      // Extract state write targets from flow steps
      const stateEntries = crossRef.stateUsageIndex.get(b.flowName) ?? []
      const writes = stateEntries.filter((e) => e.mode === 'write').map((e) => e.key)
      if (writes.length > 0) {
        targets.set(b.flowName, writes.slice(0, 2).join(', ') + (writes.length > 2 ? '...' : ''))
      }
    }
    return targets
  }, [bindings, flows, crossRef])

  // Compute position below selected element
  const updatePosition = useCallback(() => {
    if (!selectedId || !selectedBlockName || bindings.length === 0) {
      setPos(null)
      return
    }

    const canvasEl = overlayRef.current?.closest('[data-testid="canvas-viewport"]')
    if (!canvasEl) return

    const domEl = canvasEl.querySelector(
      `[data-element-id="${selectedId}"]`,
    ) as HTMLElement | null
    if (!domEl) {
      setPos(null)
      return
    }

    const visibleEl = resolveVisibleElement(domEl)
    const containerRect = overlayRef.current!.getBoundingClientRect()
    const elRect = visibleEl.getBoundingClientRect()
    const z = zoom || 1

    setPos({
      top: (elRect.top - containerRect.top) / z + elRect.height / z,
      left: (elRect.left - containerRect.left) / z,
      width: elRect.width / z,
      height: elRect.height / z,
    })
  }, [selectedId, selectedBlockName, bindings.length, zoom])

  // Track position changes
  useEffect(() => {
    updatePosition()

    const canvasEl = overlayRef.current?.closest('[data-testid="canvas-viewport"]')
    if (!canvasEl) return

    let rafId = 0
    const debouncedUpdate = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(updatePosition)
    }

    canvasEl.addEventListener('scroll', updatePosition, { passive: true })
    window.addEventListener('resize', updatePosition)
    const observer = new MutationObserver(debouncedUpdate)
    observer.observe(canvasEl, { childList: true, subtree: true })

    return () => {
      cancelAnimationFrame(rafId)
      canvasEl.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
      observer.disconnect()
    }
  }, [updatePosition])

  // Fade-in on selection, fade-out on deselection
  useEffect(() => {
    if (bindings.length > 0 && pos) {
      const timer = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(timer)
    }
    setVisible(false)
  }, [bindings.length, pos])

  if (isPreview || !selectedId || bindings.length === 0) return null

  // Determine positioning style: positioned if we have a DOM rect, hidden otherwise
  const badgeStyle: React.CSSProperties = pos
    ? {
        top: pos.top + 8,
        left: pos.left + pos.width / 2,
        transform: 'translateX(-50%)',
        opacity: visible ? 1 : 0,
        transition: visible ? 'opacity 150ms ease-in' : 'opacity 100ms ease-out',
      }
    : { top: 0, left: 0, opacity: 0, pointerEvents: 'none' }

  return (
    <div
      ref={overlayRef}
      className="pointer-events-none absolute inset-0"
      style={{ zIndex: 45 }}
      data-testid="event-flow-overlay"
    >
      <div
        className="absolute flex flex-col gap-1 items-center"
        style={badgeStyle}
      >
          {/* Dashed connector line */}
          <svg
            width="2"
            height="12"
            className="shrink-0"
            style={{ opacity: 0.5 }}
          >
            <line
              x1="1" y1="0" x2="1" y2="12"
              stroke="hsl(var(--primary))"
              strokeWidth="1"
              strokeDasharray="3 2"
            />
          </svg>

          {/* Pill badges */}
          {bindings.map((binding) => {
            const Icon = EVENT_ICONS[binding.eventType] ?? Zap
            const dataTarget = flowDataTargets.get(binding.flowName)
            return (
              <div
                key={`${binding.eventType}-${binding.flowName}`}
                className="pointer-events-auto flex items-center gap-1 rounded-full border bg-popover/95 px-2 py-0.5 text-[10px] text-foreground shadow-sm whitespace-nowrap"
                data-testid="event-flow-pill"
              >
                <Icon size={10} className="text-primary/70 shrink-0" />
                <span className="text-muted-foreground">{binding.eventType}</span>
                <ArrowRight size={8} className="text-muted-foreground/50 shrink-0" />
                <Workflow size={10} className="text-primary/70 shrink-0" />
                <span className="font-mono font-medium truncate max-w-[120px]">
                  {binding.flowName}
                </span>
                {dataTarget && (
                  <>
                    <ArrowRight size={8} className="text-muted-foreground/50 shrink-0" />
                    <Database size={10} className="text-primary/70 shrink-0" />
                    <span className="text-muted-foreground font-mono truncate max-w-[80px]">
                      {dataTarget}
                    </span>
                  </>
                )}
              </div>
            )
          })}
        </div>
    </div>
  )
}
